import dayjs, { Dayjs } from "dayjs";

import {
  ProducerTask,
  TaskCell,
  TaskColumn,
} from "../../../../app/services/taskBoard";
import type { DmThread, DmThreadStatus } from "../../../../app/services/dmThreads";
import { createMentionRegex, normalizeNfc } from "../../../../utils/text";
import {
  ATTACHMENT_LABEL_PREFIX,
  taskBoardUploadKeyOfUrl,
} from "../../../../components/markdown/markdownAttachment";

/**
 * タスクボードのセル本文（Markdown）から、期限・チェックボックス・担当者・タグを
 * 解析するためのユーティリティ群。
 *
 * いずれも「テキスト駆動」: セル本文に自然に書かれた記法を拾うだけで、
 * 専用の入力 UI を持たない（日付の期限認識と同じ思想）。
 */

// ── 自動追加チェックバック行のマーカー ────────────────────────────────

/**
 * チェックバック（プロデューサー待ち）ショットから自動追加された行を識別する
 * 予約セルキー。実在の列 ID とは衝突しない固定値で、BE は cells の任意キーを
 * そのまま保持する。描画・TSV コピー・完了判定は列 ID 基準のため影響しない。
 * チェックバック解消時、このマーカーを持つ空行だけを安全に自動削除できる。
 */
export const CB_AUTO_MARKER_KEY = "__cb_auto";

/** 自動追加チェックバック行のマーカーを持つか */
export function hasCbAutoMarker(task: Pick<ProducerTask, "cells">): boolean {
  return !!task.cells && CB_AUTO_MARKER_KEY in task.cells;
}

/** マーカーを除く全セルが空白（= ユーザー入力なし）か */
export function isTaskCellsEmpty(task: Pick<ProducerTask, "cells">): boolean {
  return Object.entries(task.cells || {}).every(
    ([key, cell]) =>
      key === CB_AUTO_MARKER_KEY || !(cell?.content ?? "").trim(),
  );
}

/** マーカーを取り除いた cells を返す（通常行への昇格用） */
export function withoutCbAutoMarker(
  cells: Record<string, TaskCell>,
): Record<string, TaskCell> {
  const next = { ...cells };
  delete next[CB_AUTO_MARKER_KEY];
  return next;
}

/** 中身のあるセル（列 + 本文） */
export interface FilledCell {
  column: TaskColumn;
  content: string;
}

/** 中身（trim 後）のある列だけを、列の並び順で取り出す */
export function collectFilledCells(
  task: Pick<ProducerTask, "cells">,
  columns: TaskColumn[],
): FilledCell[] {
  const result: FilledCell[] = [];
  for (const column of columns) {
    const content = task.cells?.[column.id]?.content ?? "";
    if (content.trim()) result.push({ column, content });
  }
  return result;
}

// ── チェックボックス ────────────────────────────────────────────────

/** GFM タスクリスト行（`- [ ] xxx` / `- [x] xxx`、`*` `+` `1.` 弾も許容） */
const TASK_CHECKBOX_LINE_RE = /^(\s*(?:[-*+]|\d+\.)\s+)\[([ xX])\]/;

export interface CheckboxStats {
  total: number;
  checked: number;
}

/** セル本文中のチェックボックス総数とチェック済み数を数える */
export function countCheckboxes(text: string): CheckboxStats {
  let total = 0;
  let checked = 0;
  for (const line of (text || "").split("\n")) {
    const m = line.match(TASK_CHECKBOX_LINE_RE);
    if (m) {
      total += 1;
      if (m[2].toLowerCase() === "x") checked += 1;
    }
  }
  return { total, checked };
}

/**
 * 行がチェック済みのタスクリスト行（`- [x] ...`）か。
 * アクティビティ集計の行単位の完了判定に使う。チェックボックスを持たない行は
 * 表の完了判定（チェックボックスなし = 未完了）に合わせて false。
 */
export function isCheckedCheckboxLine(line: string): boolean {
  const m = line.match(TASK_CHECKBOX_LINE_RE);
  return !!m && m[2].toLowerCase() === "x";
}

/**
 * セルが「完了」かどうか。チェックボックスが 1 個以上あり、その全てが
 * チェック済みのときのみ true（= グレーアウト対象）。
 */
export function isCellComplete(text: string): boolean {
  const { total, checked } = countCheckboxes(text);
  return total > 0 && checked === total;
}

/**
 * 完了フィルターの3段階。表・ガントで共有する。
 * - `incomplete`: 未完了のみ（表の既定）
 * - `complete`: 完了のみ
 * - `all`: すべて（ガントの既定。完了ぶんは淡色 + 打ち消し線で区別する）
 */
export type TaskCompletionFilter = "incomplete" | "complete" | "all";

/** 完了フィルターに照らしてこのタスクを表示するか */
export function matchesCompletionFilter(
  isComplete: boolean,
  filter: TaskCompletionFilter,
): boolean {
  if (filter === "incomplete") return !isComplete;
  if (filter === "complete") return isComplete;
  return true;
}

/**
 * タスク（行）が完了済みかどうか。行内にチェックボックスを持つセルが 1 個以上
 * あり、チェックボックスを持つ全セルが完了（全チェック済み）のときのみ true。
 * チェックボックスを持たないセル（テキストのみ・空セル）は判定に影響しない。
 */
export function isTaskComplete(
  task: Pick<ProducerTask, "cells">,
  columns: TaskColumn[],
): boolean {
  let hasCheckbox = false;
  for (const col of columns) {
    const { total, checked } = countCheckboxes(task.cells?.[col.id]?.content ?? "");
    if (total > 0) {
      hasCheckbox = true;
      if (checked < total) return false;
    }
  }
  return hasCheckbox;
}

/**
 * 本文中の N 番目（0 始まり）のチェックボックスを指定状態にトグルした本文を返す。
 * MarkdownContent はソース順にチェックボックスを描画するため、その index と一致する。
 */
export function toggleCheckboxInText(
  text: string,
  index: number,
  checked: boolean,
): string {
  let cursor = -1;
  const lines = (text || "").split("\n");
  const next = lines.map((line) => {
    const m = line.match(TASK_CHECKBOX_LINE_RE);
    if (!m) return line;
    cursor += 1;
    if (cursor !== index) return line;
    const mark = checked ? "x" : " ";
    return line.replace(TASK_CHECKBOX_LINE_RE, `${m[1]}[${mark}]`);
  });
  return next.join("\n");
}

// ── セル編集中のチェックボックス系ショートカット ──
//（mac: ⌃C / ⌃X、Windows: Alt+C / Alt+X、[]+Space は共通）
//
// TaskCell の EditTextarea（セル編集中）だけで効くテキスト変換。
// キー判定・選択範囲の復元は TaskCell 側、テキスト変換はここの純関数が担う。

/** テキスト変換の結果。呼び出し側は選択範囲を復元してから描画する */
export interface TextEditResult {
  text: string;
  selectionStart: number;
  selectionEnd: number;
}

/** チェックボックス付き行の行頭プレフィックス（`- [ ] ` 等。後続の空白 1 つまで含む） */
const CHECKBOX_PREFIX_RE = /^(\s*)(?:[-*+]|\d+\.)\s+\[[ xX]\] ?/;
/** 箇条書き行の行頭プレフィックス（`- ` / `* ` / `1. ` 等） */
const LIST_PREFIX_RE = /^\s*(?:[-*+]|\d+\.)\s+/;

/**
 * 選択範囲が掛かる行（選択が無ければカーソル行）へ行単位の変換を適用し、
 * 変換後テキストと「変換後も同じ文字を指す」選択範囲を返す。
 *
 * 変換は行頭プレフィックスの付け外しだけを想定しているため、位置の写像は
 * 「行内の位置は行の増減ぶんだけ動く（行頭側へはみ出す分は行頭へ丸める）」
 * で足りる。選択が行頭で終わる（＝次の行を 1 文字も含まない）場合、その行は
 * 対象にしない（トリプルクリック選択で次の行まで変わるのを防ぐ）。
 */
function applyToSelectedLines(
  text: string,
  selectionStart: number,
  selectionEnd: number,
  // eslint-disable-next-line no-unused-vars
  transform: (line: string) => string,
): TextEditResult {
  // 選択が行頭ちょうどで終わるときは、その行を対象から外す
  const effectiveEnd =
    selectionEnd > selectionStart && text[selectionEnd - 1] === "\n"
      ? selectionEnd - 1
      : selectionEnd;

  const startLineStart = text.lastIndexOf("\n", selectionStart - 1) + 1;
  let endLineEnd = text.indexOf("\n", effectiveEnd);
  if (endLineEnd === -1) endLineEnd = text.length;

  const before = text.slice(0, startLineStart);
  const lines = text.slice(startLineStart, endLineEnd).split("\n");
  const after = text.slice(endLineEnd);

  const isRangeSelection = selectionEnd > selectionStart;
  let mappedStart = selectionStart;
  let mappedEnd = selectionEnd;
  let oldLineStart = startLineStart;
  let shift = 0; // 処理済みの行で生じた長さ増減の累計
  const transformed = lines.map((line) => {
    const next = transform(line);
    const delta = next.length - line.length;
    const oldLineEnd = oldLineStart + line.length;
    const newLineStart = oldLineStart + shift;
    const newLineEnd = newLineStart + next.length;
    const map = (p: number) =>
      Math.min(Math.max(p + shift + delta, newLineStart), newLineEnd);
    if (selectionStart >= oldLineStart && selectionStart <= oldLineEnd) {
      // 範囲選択が行頭から始まるときは行頭に留める（選択が行全体を覆い続ける）。
      // カーソル（選択なし）は増減ぶん動かし、付けた直後のプレフィックスの
      // 後ろから入力を続けられるようにする。
      mappedStart =
        isRangeSelection && selectionStart === oldLineStart
          ? newLineStart
          : map(selectionStart);
    }
    if (selectionEnd >= oldLineStart && selectionEnd <= oldLineEnd) {
      mappedEnd = map(selectionEnd);
    }
    oldLineStart = oldLineEnd + 1; // 改行ぶん
    shift += delta;
    return next;
  });

  // 行頭終わりの選択（対象から外した行の行頭に selectionEnd がある）は、
  // 手前の行までの増減ぶんだけ平行移動する。
  if (selectionEnd >= oldLineStart) mappedEnd = selectionEnd + shift;

  return {
    text: before + transformed.join("\n") + after,
    selectionStart: mappedStart,
    selectionEnd: mappedEnd,
  };
}

/** ⌃C（Win: Alt+C）の 1 行ぶんの変換（チェックボックスの付け外し） */
function toggleCheckboxPrefixLine(line: string, todayLabel: string): string {
  const checkbox = CHECKBOX_PREFIX_RE.exec(line);
  if (checkbox) {
    const rest = line.slice(checkbox[0].length);
    // 付与時に自動挿入した本日日付だけの行は、外すと空行へ戻す（往復できる）
    if (rest.trim() === todayLabel) return checkbox[1];
    // `- [ ] 文字` → `文字`（インデントは保つ）
    return checkbox[1] + rest;
  }
  const list = LIST_PREFIX_RE.exec(line);
  if (list) {
    // `- 文字` → `- [ ] 文字`（マーカーの種類・インデントは保つ）
    const rest = line.slice(list[0].length);
    return line.slice(0, list[0].length) + "[ ] " + (rest || todayLabel);
  }
  // `文字` → `- [ ] 文字`
  //（空行は本日日付つき `- [ ] 10/24` の一発入力になり、続けて @担当 を書ける）
  const indent = /^\s*/.exec(line)?.[0] ?? "";
  const rest = line.slice(indent.length);
  return indent + "- [ ] " + (rest || todayLabel);
}

/**
 * ⌃C（Win: Alt+C）: カーソル行（複数行選択時は各行）の行頭で `- [ ] ` を
 * 付けたり外したりする。
 * 各行は独立にトグルする（付いていない行には付け、付いている行からは外す）。
 *
 * 中身が空の行に付けるときは本日日付（`M/D`、`base` から算出）まで入れ、
 * `- [ ] 10/24` のような期限行の書き出しを 1 ストロークにする。
 * 逆に「プレフィックス + 本日日付だけ」の行から外すときは日付も消して
 * 空行へ戻す（押し間違いをもう一度 ⌃C で取り消せる）。
 */
export function toggleCheckboxPrefixInLines(
  text: string,
  selectionStart: number,
  selectionEnd: number,
  base: Dayjs = dayjs(),
): TextEditResult {
  const todayLabel = base.format("M/D");
  return applyToSelectedLines(text, selectionStart, selectionEnd, (line) =>
    toggleCheckboxPrefixLine(line, todayLabel),
  );
}

/**
 * ⌃X（Win: Alt+X）: カーソル行（複数行選択時は各行）の `[ ]` と `[x]` を
 * 切り替える。
 * チェックボックスを持たない行は変更しない（長さが変わらないため選択も不動）。
 */
export function toggleCheckboxCheckedInLines(
  text: string,
  selectionStart: number,
  selectionEnd: number,
): TextEditResult {
  return applyToSelectedLines(text, selectionStart, selectionEnd, (line) => {
    const m = line.match(TASK_CHECKBOX_LINE_RE);
    if (!m) return line;
    const mark = m[2].toLowerCase() === "x" ? " " : "x";
    return line.replace(TASK_CHECKBOX_LINE_RE, `${m[1]}[${mark}]`);
  });
}

/**
 * 「[]」+スペース: 行頭（インデント可）の `[]` の直後でスペースが押されたら、
 * `[]` を `- [ ] ` へ置き換えた結果を返す（Notion 風の自動変換）。
 * 条件を満たさなければ null（通常のスペース入力に任せる）。
 */
export function convertBracketPairOnSpace(
  text: string,
  caret: number,
): { text: string; caret: number } | null {
  const lineStart = text.lastIndexOf("\n", caret - 1) + 1;
  const beforeCaret = text.slice(lineStart, caret);
  const match = /^(\s*)\[\]$/.exec(beforeCaret);
  if (!match) return null;
  const replaced = `${match[1]}- [ ] `;
  return {
    text: text.slice(0, lineStart) + replaced + text.slice(caret),
    caret: lineStart + replaced.length,
  };
}

// ── 埋め込みリンク（画像・添付ファイル） ─────────────────────────────
//
// セルには貼り付けた画像 `![](url)` や添付 `[📎 ファイル名](url)` が入る。
// URL（UUID・タイムスタンプ入りの S3 キー）やファイル名の中の `6-20` `@x`
// を期限・担当として拾わないよう、解析の前にリンクを退避する。

/** Markdown のリンク / 画像記法。group 1 = `!`、2 = ラベル、3 = URL */
const MD_LINK_RE = /(!?)\[((?:[^\]\\]|\\.)*)\]\(([^)\s]+)\)/g;
/** 退避したリンクの代わりに置く文字（私用領域。数字・区切り・@ と衝突しない） */
const LINK_PLACEHOLDER = "\uE000";
const LINK_PLACEHOLDER_RE = new RegExp(LINK_PLACEHOLDER, "g");

export interface MaskedLinks {
  /** 退避後の本文 */
  masked: string;
  /** 退避後の本文（を日付置換等で書き換えたもの）へ元のリンクを戻す */
  // eslint-disable-next-line no-unused-vars
  restore: (text: string) => string;
}

/**
 * 本文中のリンクを解析対象から外す。画像と添付はラベルごと、通常リンクは
 * URL だけを退避する（`[6/20 打合せ](url)` のラベルは本文として扱う）。
 */
export function maskLinkTargets(text: string): MaskedLinks {
  const saved: string[] = [];
  const masked = text.replace(
    MD_LINK_RE,
    (match: string, bang: string, label: string, url: string) => {
      if (bang || label.trim().startsWith(ATTACHMENT_LABEL_PREFIX)) {
        saved.push(match);
        return LINK_PLACEHOLDER;
      }
      saved.push(url);
      return `[${label}](${LINK_PLACEHOLDER})`;
    },
  );
  if (saved.length === 0) return { masked: text, restore: (t) => t };
  return {
    masked,
    restore: (t) => {
      let i = 0;
      return t.replace(LINK_PLACEHOLDER_RE, () => saved[i++] ?? "");
    },
  };
}

/**
 * タスク群の本文に埋め込まれた、このプロジェクトのセル添付（画像・添付リンク）の
 * S3 キーを重複なく昇順で返す（media-info API の問い合わせ対象）。
 * 他プロジェクトからコピーされた URL は BE が受け付けないので除く。
 */
export function collectUploadKeys(
  tasks: Pick<ProducerTask, "cells">[],
  projectId: string,
): string[] {
  const keys = new Set<string>();
  const prefix = `${projectId}/task_board/`;
  for (const task of tasks) {
    for (const cell of Object.values(task.cells || {})) {
      const content = cell?.content ?? "";
      if (!content.includes("task_board")) continue;
      for (const m of content.matchAll(MD_LINK_RE)) {
        const key = taskBoardUploadKeyOfUrl(m[3]);
        if (key?.startsWith(prefix)) keys.add(key);
      }
    }
  }
  return [...keys].sort();
}

// ── 日付（期限） ───────────────────────────────────────────────────

// NOTE: このモジュールでは正規表現の後読み（(?<!...) / (?<=...)）を使わない。
// 後読みは iOS/Safari 16.4 未満でパース時 SyntaxError になり、リテラルが
// 1 つでもバンドルに残ると SPA 全体が起動できなくなる（2026-09 の
// 「共有ノートがスマホで開けない」障害の原因）。「直前の文字が◯◯でない」の
// 判定は、マッチ後に isPrefixClear で行う。先読み（(?!...)）は問題ない。

const DATE_PATTERNS: RegExp[] = [
  // YYYY/M/D または YYYY-M-D（区切り前後の空白も許容）
  /(\d{4})\s*[/.-]\s*(\d{1,2})\s*[/.-]\s*(\d{1,2})/g,
  // YYYY年M月D日
  /(\d{4})年\s*(\d{1,2})月\s*(\d{1,2})日/g,
  // M月D日（年なし）。直前が数字でないこと（isPrefixClear で検査）
  /(\d{1,2})月\s*(\d{1,2})日/g,
  // M/D（年なし）。直前が数字・区切りでないこと（isPrefixClear で検査）。
  // 区切り前後の空白も許容（"6/ 24" など）
  /(\d{1,2})\s*[/-]\s*(\d{1,2})(?![\d/.-])/g,
];

/** M月D日 の直前に来てはいけない文字（旧 `(?<![\d])` 相当） */
const MONTH_DAY_PREFIX_EXCLUDE = /\d/;
/** M/D の直前に来てはいけない文字（旧 `(?<![\d/.-])` 相当） */
const SLASH_DATE_PREFIX_EXCLUDE = /[\d/.-]/;

/**
 * マッチ開始位置の直前の文字が exclude に一致しないこと（後読みの代替）。
 *
 * 旧実装の後読みと同値になる根拠: これらの日付パターンで「直前が数字/区切り」
 * によって棄却されたマッチの内側から始まる別のマッチ候補は、必ずその数字列の
 * 途中から始まる（= やはり直前が数字/区切り）ため、後読き版でも棄却される。
 * したがって「マッチ後に前置文字で棄却する」方式と採用集合が一致する。
 */
const isPrefixClear = (
  text: string,
  index: number,
  exclude: RegExp,
): boolean => index === 0 || !exclude.test(text.charAt(index - 1));

/**
 * 年が省略された日付の年を推定する。
 * スケジュールは 1 年先に設定することはほぼないため、常に当年として扱う。
 * 今日より前の日付は翌年に繰り上げず、そのまま「遅延（期限超過）」として扱う。
 */
function resolveYearless(month: number, day: number, base: Dayjs): Dayjs | null {
  const candidate = base.month(month - 1).date(day).startOf("day");
  return candidate.isValid() ? candidate : null;
}

/** セル/タスク本文に含まれる日付を全て抽出する（昇順・重複排除） */
export function extractDates(source: string, base: Dayjs = dayjs()): Dayjs[] {
  if (!source) return [];
  const text = maskLinkTargets(source).masked;
  const found: Dayjs[] = [];

  const pushUnique = (d: Dayjs | null) => {
    if (!d || !d.isValid()) return;
    const key = d.format("YYYY-MM-DD");
    if (!found.some((x) => x.format("YYYY-MM-DD") === key)) {
      found.push(d);
    }
  };

  // 年あり: YYYY/M/D, YYYY-M-D
  for (const m of text.matchAll(DATE_PATTERNS[0])) {
    pushUnique(
      dayjs(`${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`),
    );
  }
  // 年あり: YYYY年M月D日
  for (const m of text.matchAll(DATE_PATTERNS[1])) {
    pushUnique(
      dayjs(`${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`),
    );
  }
  // 年なし: M月D日
  for (const m of text.matchAll(DATE_PATTERNS[2])) {
    if (!isPrefixClear(text, m.index ?? 0, MONTH_DAY_PREFIX_EXCLUDE)) continue;
    pushUnique(resolveYearless(Number(m[1]), Number(m[2]), base));
  }
  // 年なし: M/D
  for (const m of text.matchAll(DATE_PATTERNS[3])) {
    if (!isPrefixClear(text, m.index ?? 0, SLASH_DATE_PREFIX_EXCLUDE)) continue;
    pushUnique(resolveYearless(Number(m[1]), Number(m[2]), base));
  }

  return found.sort((a, b) => a.valueOf() - b.valueOf());
}

/**
 * セル本文に含まれる「特定の日付」を、書式を保ったまま新しい日付に置換する。
 *
 * - 元の表記が「6/24」なら「6/25」に、「6月24日」なら「6月25日」に、
 *   「2026/6/24」なら「2026/6/25」に、と区切り文字種・年有無を維持する。
 * - 同じ本文に同日付の表記が複数あっても、置換するのは最初に見つかった 1 件のみ
 *   （ガント上のドット 1 つ = 1 表記、という対応関係を保つ）。
 * - 年なし表記は base の年で `extractDates` と同じ規則で解決した結果が
 *   `oldDate` と一致するときのみ置換する。
 * - oldDate と newDate が同日なら何もしない。
 * - 画像・添付の URL / ファイル名の中は書き換えない（maskLinkTargets）。
 */
export function replaceCellDate(
  content: string,
  oldDate: Dayjs,
  newDate: Dayjs,
  base: Dayjs = dayjs(),
): string {
  if (!content) return content;
  if (oldDate.isSame(newDate, "day")) return content;
  const { masked, restore } = maskLinkTargets(content);
  return restore(replaceDateInText(masked, oldDate, newDate, base));
}

function replaceDateInText(
  content: string,
  oldDate: Dayjs,
  newDate: Dayjs,
  base: Dayjs,
): string {

  const oldYear = oldDate.year();
  const oldMonth = oldDate.month() + 1;
  const oldDay = oldDate.date();
  const newYear = newDate.year();
  const newMonth = newDate.month() + 1;
  const newDay = newDate.date();

  let replaced = false;

  // 1) YYYY/M/D or YYYY-M-D（区切り前後の空白も許容）
  let result = content.replace(
    /(\d{4})(\s*)([/.-])(\s*)(\d{1,2})(\s*)([/.-])(\s*)(\d{1,2})/g,
    (match, y, sp1, sep1, sp2, mo, sp3, sep2, sp4, d) => {
      if (replaced) return match;
      if (
        Number(y) === oldYear &&
        Number(mo) === oldMonth &&
        Number(d) === oldDay
      ) {
        replaced = true;
        return `${newYear}${sp1}${sep1}${sp2}${newMonth}${sp3}${sep2}${sp4}${newDay}`;
      }
      return match;
    },
  );
  if (replaced) return result;

  // 2) YYYY年M月D日
  result = result.replace(
    /(\d{4})年(\s*)(\d{1,2})月(\s*)(\d{1,2})日/g,
    (match, y, sp1, mo, sp2, d) => {
      if (replaced) return match;
      if (
        Number(y) === oldYear &&
        Number(mo) === oldMonth &&
        Number(d) === oldDay
      ) {
        replaced = true;
        return `${newYear}年${sp1}${newMonth}月${sp2}${newDay}日`;
      }
      return match;
    },
  );
  if (replaced) return result;

  // 3) M月D日（年なし）。base の年で解決した結果が oldDate と一致するときのみ置換。
  //    直前が数字の並び（"912月3日" 等）は日付とみなさない（isPrefixClear）。
  result = result.replace(
    /(\d{1,2})月(\s*)(\d{1,2})日/g,
    (match, mo, sp, d, offset: number, whole: string) => {
      if (replaced) return match;
      if (!isPrefixClear(whole, offset, MONTH_DAY_PREFIX_EXCLUDE)) return match;
      const resolved = base
        .month(Number(mo) - 1)
        .date(Number(d))
        .startOf("day");
      if (resolved.isSame(oldDate, "day")) {
        replaced = true;
        return `${newMonth}月${sp}${newDay}日`;
      }
      return match;
    },
  );
  if (replaced) return result;

  // 4) M/D（年なし、区切り前後の空白を許容）。
  //    直前が数字・区切り（"2026/6/24" の "6/24" 等）は対象外（isPrefixClear）。
  result = result.replace(
    /(\d{1,2})(\s*)([/-])(\s*)(\d{1,2})(?![\d/.-])/g,
    (match, mo, sp1, sep, sp2, d, offset: number, whole: string) => {
      if (replaced) return match;
      if (!isPrefixClear(whole, offset, SLASH_DATE_PREFIX_EXCLUDE)) {
        return match;
      }
      const resolved = base
        .month(Number(mo) - 1)
        .date(Number(d))
        .startOf("day");
      if (resolved.isSame(oldDate, "day")) {
        replaced = true;
        return `${newMonth}${sp1}${sep}${sp2}${newDay}`;
      }
      return match;
    },
  );

  return result;
}

// ── 日付範囲フィルター（日付チップ: 先週 / 本日 / 明日 / 今週） ─────────

/** 日付チップの選択値。 */
export type TaskDateChip = "lastWeek" | "today" | "tomorrow" | "thisWeek";

/** タスクの日付範囲（両端の日付を含む）。 */
export interface TaskDateRange {
  start: Dayjs;
  end: Dayjs;
}

/** 週の開始日（月曜日）を返す。dayjs の既定（日曜始まり）ではなく月曜始まりで扱う。 */
function startOfWeekMonday(base: Dayjs): Dayjs {
  const d = base.startOf("day");
  return d.subtract((d.day() + 6) % 7, "day");
}

/**
 * 日付チップ → 対象の日付範囲。
 * - 先週: 先週の月曜〜日曜（やったことの振り返り）
 * - 本日 / 明日: その 1 日
 * - 今週: 本日〜今週の日曜（残りタスク）
 */
export function dateChipRange(
  chip: TaskDateChip,
  base: Dayjs = dayjs(),
): TaskDateRange {
  const today = base.startOf("day");
  const weekStart = startOfWeekMonday(today);
  switch (chip) {
    case "lastWeek":
      return {
        start: weekStart.subtract(7, "day"),
        end: weekStart.subtract(1, "day"),
      };
    case "today":
      return { start: today, end: today };
    case "tomorrow":
      return { start: today.add(1, "day"), end: today.add(1, "day") };
    case "thisWeek":
    default:
      return { start: today, end: weekStart.add(6, "day") };
  }
}

/**
 * 日付チップの選択と同時に切り替える完了フィルター。
 * 「先週」= やったこと（完了）、「今週」= 残り（未完了）、
 * 「本日」「明日」= やったこと・やることの両方（すべて）。
 */
export const DATE_CHIP_COMPLETION: Record<TaskDateChip, TaskCompletionFilter> =
  {
    lastWeek: "complete",
    today: "all",
    tomorrow: "all",
    thisWeek: "incomplete",
  };

/**
 * 期間フィルターの選択値（日付チップ + 月次の統合）。
 * 表・ガント・アクティビティ集計で共通に使う。null = すべて（絞り込みなし）。
 * month は `YYYY-MM`（期間チップの先月 / 今月 / 来月から渡る）。
 */
export type TaskPeriodFilter =
  | { kind: "chip"; chip: TaskDateChip }
  | { kind: "month"; month: string };

/**
 * 期間フィルター → 対象の日付範囲。
 * 日付チップは {@link dateChipRange}、月はその月の初日〜末日。
 * 月の形式（`YYYY-MM`）は正規表現で厳密に確認し、外れる値は null（絞り込み
 * なし）にする（`new Date()` の寛容なパースに任せると不正な文字列が有効な
 * 日付になってしまうため）。
 */
export function periodRange(
  period: TaskPeriodFilter,
  base: Dayjs = dayjs(),
): TaskDateRange | null {
  if (period.kind === "chip") return dateChipRange(period.chip, base);
  if (!/^\d{4}-\d{2}$/.test(period.month)) return null;
  const start = dayjs(`${period.month}-01`);
  if (!start.isValid()) return null;
  return { start: start.startOf("month"), end: start.endOf("month") };
}

/** 日付が範囲内（両端を含む）か。 */
export function isDateInRange(date: Dayjs, range: TaskDateRange): boolean {
  return (
    !date.isBefore(range.start, "day") && !date.isAfter(range.end, "day")
  );
}

/** タスク本文（全セル）に範囲内の日付が 1 つでも書かれているか。 */
export function taskHasDateInRange(
  task: Pick<ProducerTask, "cells">,
  range: TaskDateRange,
  base: Dayjs = dayjs(),
): boolean {
  for (const cell of Object.values(task.cells || {})) {
    const dates = extractDates(cell?.content ?? "", base);
    if (dates.some((d) => isDateInRange(d, range))) return true;
  }
  return false;
}

// ── 担当者（@メンション） ──────────────────────────────────────────
//
// 「テキスト駆動」: セル本文に書かれた `@表示名` を拾うだけで専用の入力 UI は
// 持たない。`@表示名` はメンバーの表示名と突合してユーザー紐付けに使う
// （横断ビューの担当者フィルタの母集合）。
// 正規表現は Markdown 本文のメンション装飾（MarkdownContent）と共有する
// （utils/text.ts の createMentionRegex）。

/** 本文中の `@表示名` を抽出（重複排除） */
export function extractMentions(text: string): string[] {
  if (!text) return [];
  const result: string[] = [];
  for (const m of maskLinkTargets(text).masked.matchAll(createMentionRegex())) {
    const value = m[2]?.trim();
    if (value && !result.includes(value)) result.push(value);
  }
  return result;
}

/** タスク（タイトル + 全セル）に含まれる `@表示名` を重複排除して返す */
export function collectTaskMentions(task: Pick<ProducerTask, "title" | "cells">): string[] {
  const result: string[] = [];
  const push = (names: string[]) => {
    for (const n of names) if (!result.includes(n)) result.push(n);
  };
  push(extractMentions(task.title || ""));
  for (const cell of Object.values(task.cells || {})) {
    push(extractMentions(cell?.content ?? ""));
  }
  return result;
}

/** ボード内の全タスクに登場する `@表示名` を出現順・重複排除で集約 */
export function collectBoardMentions(tasks: Pick<ProducerTask, "title" | "cells">[]): string[] {
  const result: string[] = [];
  for (const task of tasks) {
    for (const n of collectTaskMentions(task)) {
      if (!result.includes(n)) result.push(n);
    }
  }
  return result;
}

/**
 * タスクが選択中メンション（複数 = OR）に該当するか。
 * selected が空なら全件該当（フィルタなし）。
 */
export function taskMatchesMentions(
  task: Pick<ProducerTask, "title" | "cells">,
  selected: string[],
): boolean {
  if (selected.length === 0) return true;
  const mentions = collectTaskMentions(task);
  return selected.some((s) => mentions.includes(s));
}

/** {@link filterCellLinesByMentions} の結果。 */
export interface MentionLineFilterResult {
  /** 選択中の担当者の行だけを残して組み直した表示用の本文 */
  content: string;
  /**
   * 表示上のチェックボックス番号（0 始まり）→ 元本文での番号。
   * 表示本文のチェックボックスをトグルするとき、元本文の正しい位置へ
   * 変換するために使う（{@link toggleCheckboxInText} の index）。
   */
  checkboxIndexMap: number[];
}

/**
 * 担当者フィルター中のセル表示用に、本文を行単位で絞り込む。
 *
 * 選択中の `@表示名`（複数 = OR）を含む行だけを残す。行の判定は
 * {@link extractMentions} と同じ規則（リンク先 URL 中の `@` は無視）。
 *
 * 全行が該当する場合は絞り込み不要として null を返す（表示は全文のまま）。
 * 該当行が 1 行も無い場合は content が空文字の結果を返す（セルは空表示）。
 */
export function filterCellLinesByMentions(
  content: string,
  selected: string[],
): MentionLineFilterResult | null {
  if (!content || selected.length === 0) return null;
  const lines = content.split("\n");
  const keptLines: string[] = [];
  const checkboxIndexMap: number[] = [];
  let originalCheckboxIndex = 0;
  let dropped = false;
  for (const line of lines) {
    const isCheckboxLine = TASK_CHECKBOX_LINE_RE.test(line);
    const mentions = extractMentions(line);
    if (mentions.some((name) => selected.includes(name))) {
      keptLines.push(line);
      if (isCheckboxLine) checkboxIndexMap.push(originalCheckboxIndex);
    } else {
      dropped = true;
    }
    if (isCheckboxLine) originalCheckboxIndex += 1;
  }
  if (!dropped) return null;
  return { content: keptLines.join("\n"), checkboxIndexMap };
}

/**
 * 担当者フィルター中にセルが表示する本文（行絞り込み後）。
 *
 * `visibleLineMentions` は行側（TaskBoardRow）が決めた絞り込み対象で、
 * タスク名に選択中の担当者が書かれている行は null（全文表示）になる。
 * セル・列を「内容が無ければ非表示」にする判定を、TaskCell の表示絞り込みと
 * 同じ規則で行うためにここへ置く。
 */
export function mentionVisibleCellContent(
  content: string,
  visibleLineMentions: string[] | null,
): string {
  if (!visibleLineMentions || visibleLineMentions.length === 0) return content;
  const filtered = filterCellLinesByMentions(content, visibleLineMentions);
  return filtered ? filtered.content : content;
}

/**
 * 担当者フィルター中、表示対象タスクのどの行にも表示する内容が無い列を落とす。
 *
 * セル単位の非表示（{@link mentionVisibleCellContent} が空）だけだと、
 * 担当外の工程が空セルの列として横幅を占有し続けるため、列ごと畳んで
 * 「その担当者に関係する工程」だけの表にする。選択が空なら全列そのまま。
 */
export function filterColumnsByMentionContent(
  columns: TaskColumn[],
  tasks: Pick<ProducerTask, "title" | "cells">[],
  selected: string[],
): TaskColumn[] {
  if (selected.length === 0) return columns;
  return columns.filter((col) =>
    tasks.some((task) => {
      const content = task.cells?.[col.id]?.content ?? "";
      if (!content.trim()) return false;
      // タスク名に選択中の担当者が書かれた行は全セルが担当範囲（TaskBoardRow と同じ）
      const titleMentioned = extractMentions(task.title || "").some((name) =>
        selected.includes(name),
      );
      return (
        mentionVisibleCellContent(content, titleMentioned ? null : selected).trim() !==
        ""
      );
    }),
  );
}

/**
 * 行の絞り込みフィルター（担当者 / タスク名 / 日付チップ）のいずれかが
 * 適用中か。完了チップ（未完了 / 完了 / すべて）は「どの行を見るか」の
 * 切り替えであって絞り込みではないため含めない。
 *
 * 表側の「編集系の抑制」と、プロジェクト側の「タブを畳んで全ボードを並べる」
 * 判定を同じ定義に揃えるためにここへ置く。
 */
export function isTaskFilterActive(filters: {
  selectedMentions: string[];
  taskNameFilter?: string;
  dateRange?: TaskDateRange | null;
}): boolean {
  return (
    filters.selectedMentions.length > 0 ||
    !!filters.taskNameFilter?.trim() ||
    !!filters.dateRange
  );
}

// ── オファーステータス（@メンション連動） ───────────────────────────
//
// タスク名とオファーの担当範囲（scope）が一致し、@表示名がオファー相手
// （contractor.display_name）と部分一致する場合に、メンション直後へ表示する
// ステータスバッジのラベルを解決する。

/** バッジ表示対象のステータス → ラベル。ここに無いステータスは非表示。 */
const OFFER_STATUS_BADGE_LABELS: Partial<Record<DmThreadStatus, string>> = {
  contractor_pending: "オファー中",
  in_progress: "進行中",
  inspection_pending: "納品済み",
};

/** 複数オファーが一致した場合の優先順（小さいほど優先） */
const OFFER_STATUS_PRIORITY: Partial<Record<DmThreadStatus, number>> = {
  in_progress: 0,
  inspection_pending: 1,
  contractor_pending: 2,
};

/** マッチングに使うオファーの最小形 */
export type OfferStatusSource = Pick<
  DmThread,
  "scope" | "status" | "contractor" | "updated_at"
>;

/**
 * タスク名 × @表示名 に対応するオファーのステータスバッジラベルを返す。
 *
 * - スコープ一致: タスク名とオファーの `scope` が trim + NFC 正規化後に完全一致
 * - 相手一致: `@表示名` と `contractor.display_name` の**部分一致**
 *   （例: `@大脇` と `大脇剛` は一致）
 * - 対象ステータス: オファー中（contractor_pending）/ 進行中（in_progress）/
 *   納品済み（inspection_pending）。完了・クローズ・不成立・下書きは非表示。
 * - 複数一致時: 進行系ステータスを優先し、同ステータス内は `updated_at` の新しい順。
 */
export function findOfferStatusLabel(
  offers: OfferStatusSource[],
  taskTitle: string,
  mentionName: string,
): string | null {
  const title = normalizeNfc((taskTitle || "").trim());
  const name = normalizeNfc((mentionName || "").trim());
  if (!title || !name) return null;

  const candidates = offers.filter((offer) => {
    if (!OFFER_STATUS_BADGE_LABELS[offer.status]) return false;
    const scope = normalizeNfc((offer.scope || "").trim());
    if (!scope || scope !== title) return false;
    const display = normalizeNfc(
      (offer.contractor?.display_name || "").trim(),
    );
    if (!display) return false;
    return display.includes(name) || name.includes(display);
  });
  if (candidates.length === 0) return null;

  candidates.sort((a, b) => {
    const pa = OFFER_STATUS_PRIORITY[a.status] ?? 99;
    const pb = OFFER_STATUS_PRIORITY[b.status] ?? 99;
    if (pa !== pb) return pa - pb;
    return (a.updated_at || "") < (b.updated_at || "") ? 1 : -1;
  });
  return OFFER_STATUS_BADGE_LABELS[candidates[0].status] ?? null;
}

// ── 並び替え（最遅日付・昇順 = 期日が近いものが先頭） ───────────────

/**
 * セル本文に含まれる最も遅い（未来側の）日付を YYYY-MM-DD で返す。
 * 日付が無ければ null。年省略の日付は当年として扱う
 * （過ぎた日付は翌年に繰り上げず遅延扱い）。
 */
function latestDateIn(content: string, base: Dayjs): string | null {
  const dates = extractDates(content, base);
  // extractDates は昇順ソート済みのため末尾が最遅。
  return dates.length > 0 ? dates[dates.length - 1].format("YYYY-MM-DD") : null;
}

/**
 * タスクの代表日付を算出する。全セルを通じて最も遅い日付を YYYY-MM-DD で
 * 返す。日付が無ければ null。
 */
export function computeTaskLatestDate(
  task: Pick<ProducerTask, "cells">,
  base: Dayjs = dayjs(),
): string | null {
  let latest: string | null = null;
  for (const cell of Object.values(task.cells || {})) {
    const d = latestDateIn(cell?.content ?? "", base);
    if (d && (!latest || d > latest)) latest = d;
  }
  return latest;
}

/**
 * タスクの「一番右の日付」を算出する。列の並び順で最も右にある、日付を含む
 * セルを探し、そのセル内の最遅日付を YYYY-MM-DD で返す。日付が無ければ null。
 * ショット連動（次の納品日の自動更新）の基準値に使う。
 */
export function computeTaskRightmostDate(
  task: Pick<ProducerTask, "cells">,
  columns: TaskColumn[],
  base: Dayjs = dayjs(),
): string | null {
  for (let i = columns.length - 1; i >= 0; i -= 1) {
    const d = latestDateIn(task.cells?.[columns[i].id]?.content ?? "", base);
    if (d) return d;
  }
  return null;
}

/**
 * ボード全タスクの最遅日付（= プロジェクトの最終期日）を YYYY-MM-DD で返す。
 * どのタスクにも日付が無ければ null。完了・未完了は区別しない
 * （フィルター切替でプロジェクトの並び順が変わらないようにするため）。
 */
export function computeBoardLatestDate(
  tasks: Pick<ProducerTask, "cells">[],
  base: Dayjs = dayjs(),
): string | null {
  let latest: string | null = null;
  for (const task of tasks) {
    const d = computeTaskLatestDate(task, base);
    if (d && (!latest || d > latest)) latest = d;
  }
  return latest;
}

/**
 * タスクを各レコードの「最遅日付」の昇順（最遅が近いものが先頭）で並べる。
 * 日付を持たないタスクは末尾。
 *
 * 同日付内は**作成日時の昇順**（先に作った行が上）。表全体が「上が過去 / 下が
 * 最新」の昇順なので、同点もそれに揃える。ここを降順にすると、行の分割や複製、
 * 行の追加のように短時間で複数行を作る操作で、作った順と逆に積み上がってしまう
 * （1 行を A/B/C/D に分割すると D C B A に見える）。
 *
 * 作成日時が同値のときは 0 を返し、Array.prototype.sort の安定性に任せて
 * 渡された順序をそのまま保つ。
 */
export function sortTasksByLatestDate(
  tasks: ProducerTask[],
  base: Dayjs = dayjs(),
): ProducerTask[] {
  const createdKey = (t: ProducerTask) => t.created || t.modified || "";
  return [...tasks].sort((a, b) => {
    const da = computeTaskLatestDate(a, base);
    const db = computeTaskLatestDate(b, base);
    if (da !== db) {
      if (!da) return 1;
      if (!db) return -1;
      return da < db ? -1 : 1;
    }
    const ca = createdKey(a);
    const cb = createdKey(b);
    if (ca === cb) return 0;
    return ca < cb ? -1 : 1;
  });
}

/**
 * タスクを「完了が先（上）、未完了が後（下）」の時系列昇順に一本化して並べる。
 * メッセージ欄と同じく「下が最新、上へ遡るほど過去」という読み方にする:
 *
 * - 完了: 期日の昇順（古い完了が先頭、直近の完了が未完了の直前）。
 *   日付を持たない完了は「最も古い」扱いで完了群の先頭に寄せる
 *   （昇順の既定では末尾 = 直近完了の位置に見えてしまうため）
 * - 未完了: 期日の昇順（期日が近いものが先頭 = 従来どおり）。日付なしは末尾
 *
 * 全体として上から「古い完了 → 直近の完了 → 期日が近い未完了 → 遠い未完了」
 * と並ぶため、末尾（最新側）から描画して上スクロールで過去へ遡る段階描画と
 * 噛み合う。
 *
 * `treatAsIncomplete` に含まれる ID は完了済みでも未完了群として扱う。
 * 編集直後に完了になった行を画面に残す際、行が完了群（表の上側）へ飛ばずに
 * 元の位置に留まるようにするために使う。
 */
export function sortTasksByCompletionThenDate(
  tasks: ProducerTask[],
  columns: TaskColumn[],
  base: Dayjs = dayjs(),
  treatAsIncomplete?: ReadonlySet<string>,
): ProducerTask[] {
  const incomplete: ProducerTask[] = [];
  const complete: ProducerTask[] = [];
  for (const task of tasks) {
    const asComplete =
      isTaskComplete(task, columns) && !treatAsIncomplete?.has(task.id);
    (asComplete ? complete : incomplete).push(task);
  }
  const undatedComplete: ProducerTask[] = [];
  const datedComplete: ProducerTask[] = [];
  for (const task of sortTasksByLatestDate(complete, base)) {
    (computeTaskLatestDate(task, base) ? datedComplete : undatedComplete).push(
      task,
    );
  }
  return [
    ...undatedComplete,
    ...datedComplete,
    ...sortTasksByLatestDate(incomplete, base),
  ];
}

// ── 列ごとの並び替え（ヘッダー操作によるアドホックソート） ──────────────

export type TaskSortDirection = "asc" | "desc";

/** ヘッダー操作による並び替え状態。`key` はタスク名列のキーまたは列 ID */
export interface TaskSortState {
  key: string;
  direction: TaskSortDirection;
}

/** 並び替え方向を示す矢印アイコン（Material Symbols）の名前 */
export const TASK_SORT_ARROW_ICON: Record<TaskSortDirection, string> = {
  asc: "arrow_upward",
  desc: "arrow_downward",
};

/**
 * タスクを 1 列の内容で並べ替える（ヘッダークリックによる一時的な並び替え）。
 *
 * - 日付を含むセルは日付で比較する（このボードのセルは日付が主役のため）
 * - 日付を持たない、または日付が同じセル同士は本文の文字列で比較する
 * - 空のセルは昇順・降順とも末尾に置く（スプレッドシートの慣習）
 * - 比較が同値なら渡された順序を保つ（= 既定の期日順が副キーになる）
 *
 * `titleKey` と一致するキーのときはタスク名で比較する。
 */
export function sortTasksByColumn(
  tasks: ProducerTask[],
  sort: TaskSortState,
  titleKey: string,
  base: Dayjs = dayjs(),
): ProducerTask[] {
  const sortValueOf = (task: ProducerTask) => {
    if (sort.key === titleKey) {
      return { date: null as string | null, text: (task.title || "").trim() };
    }
    const content = (task.cells?.[sort.key]?.content ?? "").trim();
    return { date: latestDateIn(content, base), text: content };
  };
  const factor = sort.direction === "asc" ? 1 : -1;

  return [...tasks].sort((a, b) => {
    const va = sortValueOf(a);
    const vb = sortValueOf(b);

    const emptyA = !va.date && !va.text;
    const emptyB = !vb.date && !vb.text;
    if (emptyA !== emptyB) return emptyA ? 1 : -1;
    if (emptyA && emptyB) return 0;

    if (va.date && vb.date) {
      if (va.date !== vb.date) return va.date < vb.date ? -factor : factor;
    } else if (va.date || vb.date) {
      // 日付入りは「テキストのみ」より前（昇順時）に置く
      return va.date ? -factor : factor;
    }

    const cmp = va.text.localeCompare(vb.text, "ja", { numeric: true });
    return cmp === 0 ? 0 : cmp * factor;
  });
}

// ── クリップボード（Excel / CSV） ───────────────────────────────────
//
// ホーム画面のスケジュール（ShotTable）と同じ要領で、タブ区切り（Excel/
// スプレッドシート）または CSV を 2 次元配列にパースする。

const sanitizeCell = (value: string): string =>
  (value || "").replace(/[\t\r\n]+/g, " ").trim();

/** 1 行を区切り文字で分割する（ダブルクォート対応の簡易パーサ） */
function splitLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"' && current === "") {
      inQuotes = true;
    } else if (ch === delimiter) {
      cells.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells.map((c) => c.trim());
}

/**
 * クリップボードのテキストを 2 次元配列にパースする。
 * タブを含めば TSV（Excel/スプレッドシート）、無ければ CSV として扱う。
 */
export function parseClipboardTable(text: string): string[][] {
  const delimiter = text.includes("\t") ? "\t" : ",";
  return text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((line) => line.trim() !== "")
    .map((line) => splitLine(line, delimiter));
}

/** 複数セル（表形式）の貼り付けかどうか */
export function isTabularClipboardText(text: string): boolean {
  if (text.includes("\t")) return true;
  const lines = text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((l) => l.trim() !== "");
  return lines.length > 1;
}

/** 表ボードのヘッダー行（1 列目が「タスク」系）かどうか */
export function isTaskHeaderRow(cells: string[]): boolean {
  const head = (cells[0] ?? "").trim();
  return head === "タスク" || head === "タスク名" || head.toLowerCase() === "task";
}

/** 表全体を Excel / スプレッドシート互換の TSV に変換する */
export function buildBoardTsv(
  columns: TaskColumn[],
  tasks: ProducerTask[],
): string {
  const header = ["タスク", ...columns.map((c) => sanitizeCell(c.title))];
  const lines = tasks.map((t) => [
    sanitizeCell(t.title),
    ...columns.map((c) => sanitizeCell(t.cells?.[c.id]?.content ?? "")),
  ]);
  return [header, ...lines].map((row) => row.join("\t")).join("\n");
}

// ── 並列グループ化（縦積み表示用） ───────────────────────────────────
//
// 列を「直列の境界」で区切ってグループ化する。縦積み表示では、連続する並列列
// （parallel=true）を 1 グループ = 1 列幅にまとめて縦に積む。構造（columns 配列・
// 各列の parallel フラグ）は変更せず、表示のレイアウト計算にのみ使う。

/**
 * 列を「並列グループ」に分割する。先頭列、または parallel=false（直列）の列で
 * 新しいグループを開始し、parallel=true（並列）の列は直前グループへ束ねる。
 * Gantt の `buildBarSegments` と同じ分断規則。
 *
 * 例: A(直列) B(直列) C(並列) D(並列) E(直列)
 *   → [[A], [B, C, D], [E]]
 */
export function groupColumnsByParallel(columns: TaskColumn[]): TaskColumn[][] {
  const groups: TaskColumn[][] = [];
  let current: TaskColumn[] = [];
  columns.forEach((col, i) => {
    if (i === 0 || !col.parallel) {
      if (current.length > 0) groups.push(current);
      current = [col];
    } else {
      current.push(col);
    }
  });
  if (current.length > 0) groups.push(current);
  return groups;
}

// ── 行の分割 ────────────────────────────────────────────────────────

/** 分割後の 1 行ぶん（タスク名 + 列 ID ごとのセル） */
export interface SplitTaskRow {
  title: string;
  cells: Record<string, TaskCell>;
}

/**
 * 1 行を「セル内の改行」で複数行へ分割した結果を返す。
 *
 * 全セルを改行で割り、各セルの N 行目同士を集めて新しい N 行目にする。
 * タスク名は全行へ複製する（どの行がどのタスクのものか分かるようにするため）。
 * 全セルが空になる行は結果に含めない。
 *
 * セルは本文だけを持ち直すので、チェックバック自動追加行のマーカーのような
 * 本文以外の属性は引き継がない（分割後はいずれも通常の行として扱う）。
 *
 * 分割の意味が無いとき（改行が無い / 結果が 1 行以下）は空配列を返すので、
 * 呼び出し側はそのままメニューの活性判定に使える。
 */
export function splitTaskByLines(
  task: Pick<ProducerTask, "title" | "cells">,
  columns: TaskColumn[],
): SplitTaskRow[] {
  const linesByColumn = new Map<string, string[]>();
  let maxLines = 0;
  for (const col of columns) {
    const content = task.cells?.[col.id]?.content ?? "";
    const lines = content.split("\n");
    linesByColumn.set(col.id, lines);
    if (content.trim()) maxLines = Math.max(maxLines, lines.length);
  }
  if (maxLines <= 1) return [];

  const rows: SplitTaskRow[] = [];
  for (let i = 0; i < maxLines; i += 1) {
    const cells: Record<string, TaskCell> = {};
    let hasContent = false;
    for (const col of columns) {
      const line = linesByColumn.get(col.id)?.[i] ?? "";
      if (!line.trim()) continue;
      cells[col.id] = { content: line };
      hasContent = true;
    }
    if (hasContent) rows.push({ title: task.title || "", cells });
  }
  return rows.length > 1 ? rows : [];
}

// ── 行の移動（別ボードへ） ──────────────────────────────────────────
//
// 実際の移動（セルの列対応付け・不足列の追加・sort_order 付け替え）は BE の
// move エンドポイントが行う。FE は事前に「移動先に無い列」を列挙して
// 確認モーダルを出すためだけに同じ照合規則でシミュレートする。

/** 列タイトルを照合用に正規化する（trim + NFC）。BE の `_normalized_column_title` と同じ規則 */
function normalizeColumnTitle(title: string): string {
  return normalizeNfc((title || "").trim());
}

export interface TaskMovePlan {
  /**
   * 移動先に対応する列が無い移動元列のタイトル（移動元の列順）。
   * 空なら確認なしで移動できる。
   */
  missingTitles: string[];
}

/**
 * 行移動の列対応を事前判定する。
 *
 * BE と同じ規則:
 * - タイトルは trim + NFC 正規化で比較する。
 * - 同名列が複数ある場合は order 順で n 番目同士を対応させる。
 * - 本文が空のセルは対応付けの対象外（移動時に破棄される）。
 */
export function planTaskMove(
  task: Pick<ProducerTask, "cells">,
  sourceColumns: TaskColumn[],
  targetColumns: TaskColumn[],
): TaskMovePlan {
  // 移動先の同名列の残数。order 順に 1 列ずつ消費する（n 番目同士の対応）。
  const remaining = new Map<string, number>();
  for (const col of [...targetColumns].sort((a, b) => a.order - b.order)) {
    const key = normalizeColumnTitle(col.title);
    remaining.set(key, (remaining.get(key) ?? 0) + 1);
  }

  const missingTitles: string[] = [];
  for (const col of [...sourceColumns].sort((a, b) => a.order - b.order)) {
    const content = task.cells?.[col.id]?.content ?? "";
    if (!content.trim()) continue;
    const key = normalizeColumnTitle(col.title);
    const left = remaining.get(key) ?? 0;
    if (left > 0) {
      remaining.set(key, left - 1);
    } else {
      missingTitles.push(col.title);
    }
  }
  return { missingTitles };
}
